import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  Checkbox,
  FormControlLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  LinearProgress,
  Chip,
  IconButton,
  Popover,
  Divider,
  Badge,
  CircularProgress,
  Pagination,
} from '@mui/material';
import { keyframes } from '@mui/material/styles';
import {
  TrendingUp,
  TrendingDown,
  Info,
  Check,
  Edit3,
  Clock,
  X,
  Eye,
  RefreshCw,
  FileText,
  Download,
  Link2,
  Loader2,
  Building2,
} from 'lucide-react';
import { useAuth } from '../../../../AuthProvider';
import InsightDrawer from './InsightDrawer';

const spin = keyframes`
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
`;

// Utility for $/SF
const getRentPerSqft = (rent, sqft) => {
  if (!rent || !sqft || sqft === 0) return '-';
  return `$${(rent / sqft).toFixed(2)}`;
};

const ActionInbox = () => {
  const { getAccessToken } = useAuth();
  const [selectedIds, setSelectedIds] = useState([]);
  const [showAdjustModal, setShowAdjustModal] = useState(false);
  const [showSnoozeModal, setShowSnoozeModal] = useState(false);
  const [showDismissModal, setShowDismissModal] = useState(false);
  const [selectedOpportunity, setSelectedOpportunity] = useState(null);
  const [showInsightDrawer, setShowInsightDrawer] = useState(false);
  const [showReportModal, setShowReportModal] = useState(false);
  const [reportGenerating, setReportGenerating] = useState(false);
  const [reportReady, setReportReady] = useState(false);
  const [pdfLink, setPdfLink] = useState(null);
  const [infoAnchor, setInfoAnchor] = useState(null);
  const [shareUrl, setShareUrl] = useState(null);
  const [showShareModal, setShowShareModal] = useState(false);
  const [opportunities, setOpportunities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    totalPages: 0,
  });
  const [summary, setSummary] = useState({
    totalOpportunities: 0,
    totalUplift: 0,
    averageDelta: 0,
  });
  const [hasNewComps, setHasNewComps] = useState(false);
  const [hasCustomComps, setHasCustomComps] = useState(false);
  const [showRecalculateDialog, setShowRecalculateDialog] = useState(false);
  const [recalculating, setRecalculating] = useState(false);

  useEffect(() => {
    fetchOpportunities();
  }, [page]);

  useEffect(() => {
    if (!loading && !recalculating) {
      checkForNewComps();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading]);

  const fetchOpportunities = async () => {
    try {
      setLoading(true);
      setError(null);
      const accessToken = getAccessToken();
      const url = new URL(
        `${process.env.REACT_APP_NODE_URL}/comps/opportunities`,
      );
      url.searchParams.append('page', page.toString());
      url.searchParams.append('limit', '20');

      const response = await fetch(url.toString(), {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${accessToken}`,
        },
      });

      const data = await response.json();

      if (!response.ok) {
        const errorMessage =
          data.message || `HTTP error! status: ${response.status}`;
        if (response.status === 404) {
          setError('noBuildings');
          setOpportunities([]);
          setSummary({
            totalOpportunities: 0,
            totalUplift: 0,
            averageDelta: 0,
          });
          return;
        }
        throw new Error(errorMessage);
      }

      if (data.status && data.data) {
        const updatedOpportunities = data.data.opportunities || [];
        setOpportunities(updatedOpportunities);
        setSummary(
          data.data.summary || {
            totalOpportunities: 0,
            totalUplift: 0,
            averageDelta: 0,
          },
        );
        if (data.data.pagination) {
          setPagination(data.data.pagination);
        }

        if (selectedOpportunity) {
          const updatedOpportunity = updatedOpportunities.find(
            opp => opp.id === selectedOpportunity.id,
          );
          if (updatedOpportunity) {
            setSelectedOpportunity(updatedOpportunity);
          }
        }
      } else {
        const errorMessage = data.message || 'Failed to fetch opportunities';
        if (errorMessage.includes('No buildings found with units')) {
          setError('noBuildings');
          setOpportunities([]);
          setSummary({
            totalOpportunities: 0,
            totalUplift: 0,
            averageDelta: 0,
          });
          return;
        }
        throw new Error(errorMessage);
      }
    } catch (err) {
      console.error('Error fetching opportunities:', err);
      if (
        err.message &&
        err.message.includes('No buildings found with units')
      ) {
        setError('noBuildings');
      } else {
        setError(err.message || 'Failed to fetch opportunities');
      }
      setOpportunities([]);
    } finally {
      setLoading(false);
    }
  };

  const checkForNewComps = async () => {
    try {
      const accessToken = getAccessToken();
      const response = await fetch(
        `${process.env.REACT_APP_NODE_URL}/comps/check-new-comps`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${accessToken}`,
          },
        },
      );

      const data = await response.json();

      if (response.ok && data.status && data.data) {
        const { hasNewComps: newCompsFound } = data.data;
        setHasNewComps(newCompsFound);

        if (newCompsFound) {
          await checkUserHasCustomComps();
        }
      }
    } catch (err) {
      console.error('Error checking for new comps:', err);
    }
  };

  const checkUserHasCustomComps = async () => {
    try {
      const accessToken = getAccessToken();
      const response = await fetch(
        `${process.env.REACT_APP_NODE_URL}/comps/has-custom-comps`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${accessToken}`,
          },
        },
      );

      const data = await response.json();

      if (response.ok && data.status && data.data) {
        const { hasCustomComps: userHasCustomComps } = data.data;
        setHasCustomComps(userHasCustomComps);

        if (userHasCustomComps) {
          setShowRecalculateDialog(true);
        } else {
          handleAutoRecalculate();
        }
      }
    } catch (err) {
      console.error('Error checking for custom comps:', err);
    }
  };

  const handleAutoRecalculate = async () => {
    try {
      setRecalculating(true);
      const accessToken = getAccessToken();
      const url = new URL(
        `${process.env.REACT_APP_NODE_URL}/buildings/process-rent-grids`,
      );
      url.searchParams.append('force', 'true');

      const response = await fetch(url.toString(), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${accessToken}`,
        },
      });

      const data = await response.json();

      if (response.ok && data.status) {
        await fetchOpportunities();
      }
    } catch (err) {
      console.error('Error auto-recalculating rent grids:', err);
    } finally {
      setRecalculating(false);
    }
  };

  const handleManualRecalculate = async () => {
    setShowRecalculateDialog(false);
    await handleAutoRecalculate();
  };

  const totalDelta = Math.max(0, summary.totalUplift || 0);
  const avgDelta = summary.averageDelta || 0;

  const scrapingProgress = [
    {
      building: 'Sunset Apartments',
      status: 'complete',
      progress: 100,
      compsFound: 45,
      compsTarget: 45,
    },
    {
      building: 'Oak Ridge',
      status: 'in-progress',
      progress: 67,
      compsFound: 32,
      compsTarget: 48,
    },
    {
      building: 'Pine Valley',
      status: 'pending',
      progress: 0,
      compsFound: 0,
      compsTarget: 40,
    },
  ];

  const handleSelectAll = () => {
    if (selectedIds.length === opportunities.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(opportunities.map(o => o.id));
    }
  };

  const handleSelect = id => {
    setSelectedIds(prev =>
      prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id],
    );
  };

  const handleGenerateReport = async () => {
    if (!selectedOpportunity) {
      alert('No opportunity selected for report generation.');
      return;
    }

    setReportGenerating(true);
    setReportReady(false);
    setPdfLink(null);

    try {
      const accessToken = getAccessToken();
      const requestBody = {
        rentGridId: selectedOpportunity.rentGridId,
        opportunity: {
          building: selectedOpportunity.building,
          unit: selectedOpportunity.unit,
          bed: selectedOpportunity.bed,
          bath: selectedOpportunity.bath,
          sqft: selectedOpportunity.sqft,
          currentRent: selectedOpportunity.currentRent,
          recommendedRent: selectedOpportunity.recommendedRent,
          delta: selectedOpportunity.delta,
          deltaPercent: selectedOpportunity.deltaPercent,
        },
      };

      const response = await fetch(
        `${process.env.REACT_APP_NODE_URL}/rent-advisor-pdf/generate`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${accessToken}`,
          },
          body: JSON.stringify(requestBody),
        },
      );

      if (!response.ok) {
        let errorMessage = `HTTP error! status: ${response.status}`;
        try {
          const errorData = await response.json();
          errorMessage = errorData.message || errorMessage;
        } catch (e) {
          // If JSON parsing fails, use the default error message
        }
        throw new Error(errorMessage);
      }

      const data = await response.json();
      if (data.status && data.link) {
        setPdfLink(data.link);
        setReportGenerating(false);
        setReportReady(true);
      } else {
        throw new Error(data.message || 'Failed to generate PDF');
      }
    } catch (err) {
      console.error('Error generating PDF:', err);
      setReportGenerating(false);
      alert(
        err.message ||
          'Failed to generate PDF report. Please ensure the server has been restarted.',
      );
    }
  };

  const handleApprove = opportunity => {
    setSelectedOpportunity(opportunity);
    setShowReportModal(true);
    if (opportunity.pdfReportUrl) {
      setPdfLink(opportunity.pdfReportUrl);
      setReportReady(true);
      setReportGenerating(false);
    } else {
      setReportGenerating(false);
      setReportReady(false);
      setPdfLink(null);
    }
  };

  const handleAdjust = opportunity => {
    setSelectedOpportunity(opportunity);
    setShowAdjustModal(true);
  };

  const handleSnooze = opportunity => {
    setSelectedOpportunity(opportunity);
    setShowSnoozeModal(true);
  };

  const handleDismiss = opportunity => {
    setSelectedOpportunity(opportunity);
    setShowDismissModal(true);
  };

  const handleViewComps = opportunity => {
    setSelectedOpportunity(opportunity);
    setShowInsightDrawer(true);
  };

  const getConfidenceColor = confidence => {
    switch (confidence) {
      case 'high':
        return { bg: '#E8F0F1', text: '#39585C', border: '#4FA490' };
      case 'medium':
        return { bg: '#FEF3C7', text: '#92400E', border: '#F59E0B' };
      case 'low':
        return { bg: '#FEE2E2', text: '#991B1B', border: '#EF4444' };
      default:
        return { bg: '#F3F4F6', text: '#374151', border: '#9CA3AF' };
    }
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100%',
          backgroundColor: '#F9F9F9',
        }}
      >
        <CircularProgress sx={{ color: '#39585C' }} />
      </Box>
    );
  }

  if (error) {
    const isNoBuildingsError = error === 'noBuildings';

    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100%',
          backgroundColor: '#F9F9F9',
          gap: '1rem',
          padding: '2rem',
        }}
      >
        {isNoBuildingsError ? (
          <>
            <Building2 size={48} color='#6B7280' />
            <Typography
              sx={{
                fontSize: '1rem',
                fontWeight: 600,
                color: '#111827',
                textAlign: 'center',
              }}
            >
              No Opportunities Found
            </Typography>
            <Typography
              sx={{
                fontSize: '0.875rem',
                color: '#6B7280',
                textAlign: 'center',
                maxWidth: '400px',
              }}
            >
              You haven't added any buildings with units yet. Add buildings to
              start seeing rent optimization opportunities.
            </Typography>
          </>
        ) : (
          <>
            <Typography sx={{ color: '#EF4444', fontSize: '0.875rem' }}>
              {error}
            </Typography>
            <Button
              variant='outlined'
              onClick={fetchOpportunities}
              sx={{
                textTransform: 'none',
                borderColor: '#D1D5DB',
                color: '#374151',
              }}
            >
              Retry
            </Button>
          </>
        )}
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        backgroundColor: '#F9F9F9',
      }}
    >
      <Box
        sx={{
          borderBottom: '1px solid #E5E7EB',
          backgroundColor: '#FFFFFF',
          paddingX: { xs: '1rem', md: '1.5rem' },
          paddingY: '1rem',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: { xs: 'start', sm: 'center' },
            justifyContent: 'space-between',
            flexDirection: { xs: 'column', sm: 'row' },
            gap: { xs: '1rem', sm: 0 },
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: { xs: '0.75rem', sm: '1.5rem' },
              flexWrap: 'wrap',
            }}
          >
            <Box>
              <Typography
                sx={{
                  fontSize: { xs: '1.25rem', md: '1.5rem' },
                  fontWeight: 700,
                  color: '#111827',
                }}
              >
                {opportunities.length}
              </Typography>
              <Typography
                sx={{
                  fontSize: { xs: '0.75rem', md: '0.875rem' },
                  color: '#6B7280',
                }}
              >
                Opportunities
              </Typography>
            </Box>
            <Divider
              orientation='vertical'
              flexItem
              sx={{
                height: { xs: '30px', sm: '40px' },
                display: { xs: 'none', sm: 'block' },
              }}
            />
            <Box>
              <Typography
                sx={{
                  fontSize: { xs: '1.25rem', md: '1.5rem' },
                  fontWeight: 700,
                  color: totalDelta > 0 ? '#4FA490' : '#6B7280',
                }}
              >
                {totalDelta > 0 ? '+' : ''}${totalDelta.toLocaleString()}/mo
              </Typography>
              <Typography
                sx={{
                  fontSize: { xs: '0.75rem', md: '0.875rem' },
                  color: '#6B7280',
                }}
              >
                Potential Uplift
              </Typography>
            </Box>
            <Divider
              orientation='vertical'
              flexItem
              sx={{
                height: { xs: '30px', sm: '40px' },
                display: { xs: 'none', sm: 'block' },
              }}
            />
            <Box>
              <Typography
                sx={{
                  fontSize: { xs: '1.25rem', md: '1.5rem' },
                  fontWeight: 700,
                  color: '#111827',
                }}
              >
                +${avgDelta}
              </Typography>
              <Typography
                sx={{
                  fontSize: { xs: '0.75rem', md: '0.875rem' },
                  color: '#6B7280',
                }}
              >
                Average Delta
              </Typography>
            </Box>
            <Divider
              orientation='vertical'
              flexItem
              sx={{
                height: { xs: '30px', sm: '40px' },
                display: { xs: 'none', sm: 'block' },
              }}
            />
          </Box>
        </Box>
      </Box>

      {selectedIds.length > 0 && (
        <Box
          sx={{
            borderBottom: '1px solid #E5E7EB',
            backgroundColor: '#E8F0F1',
            paddingX: { xs: '1rem', md: '1.5rem' },
            paddingY: '0.75rem',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              flexDirection: { xs: 'column', sm: 'row' },
              gap: { xs: '0.75rem', sm: 0 },
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
              <Checkbox
                checked={selectedIds.length === opportunities.length}
                onChange={handleSelectAll}
                size='small'
              />
              <Typography
                sx={{
                  fontSize: { xs: '0.75rem', md: '0.875rem' },
                  fontWeight: 500,
                  color: '#111827',
                }}
              >
                {selectedIds.length} selected
              </Typography>
            </Box>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                flexWrap: 'wrap',
                width: { xs: '100%', sm: 'auto' },
                justifyContent: { xs: 'flex-start', sm: 'flex-end' },
              }}
            >
              <Button
                size='small'
                variant='contained'
                onClick={() => {
                  const firstSelectedId = selectedIds[0];
                  const opportunity = opportunities.find(
                    opp => opp.id === firstSelectedId,
                  );
                  if (opportunity) {
                    handleApprove(opportunity);
                  }
                }}
                startIcon={<Check size={16} />}
                sx={{
                  backgroundColor: '#39585C',
                  textTransform: 'none',
                  fontSize: '0.75rem',
                  '&:hover': { backgroundColor: '#2A3F42' },
                }}
              >
                Approve {selectedIds.length}
              </Button>
              <Button
                size='small'
                variant='outlined'
                onClick={() =>
                  selectedIds.forEach(id =>
                    handleSnooze(opportunities.find(opp => opp.id === id)),
                  )
                }
                startIcon={<Clock size={16} />}
                sx={{
                  textTransform: 'none',
                  fontSize: '0.75rem',
                  borderColor: '#D1D5DB',
                  color: '#374151',
                }}
              >
                Snooze
              </Button>
              <Button
                size='small'
                variant='outlined'
                onClick={() =>
                  selectedIds.forEach(id =>
                    handleDismiss(opportunities.find(opp => opp.id === id)),
                  )
                }
                startIcon={<X size={16} />}
                sx={{
                  textTransform: 'none',
                  fontSize: '0.75rem',
                  borderColor: '#D1D5DB',
                  color: '#374151',
                }}
              >
                Dismiss
              </Button>
              <Button
                size='small'
                variant='outlined'
                sx={{
                  textTransform: 'none',
                  fontSize: '0.75rem',
                  borderColor: '#D1D5DB',
                  color: '#374151',
                }}
              >
                Export Selected
              </Button>
            </Box>
          </Box>
        </Box>
      )}

      <Box
        sx={{
          flex: 1,
          overflow: 'auto',
          padding: { xs: '1rem', md: '1.5rem' },
          backgroundColor: '#F9F9F9',
        }}
      >
        <Box
          sx={{
            maxWidth: '1536px',
            marginX: 'auto',
            display: 'flex',
            flexDirection: 'column',
            gap: '0.75rem',
          }}
        >
          {opportunities.map(opportunity => {
            const confidenceColors = getConfidenceColor(opportunity.confidence);
            return (
              <Card
                key={opportunity.id}
                sx={{
                  padding: '1rem',
                  '&:hover': {
                    boxShadow: 3,
                  },
                  transition: 'box-shadow 0.2s',
                }}
              >
                <Box
                  sx={{ display: 'flex', alignItems: 'start', gap: '0.75rem' }}
                >
                  <Checkbox
                    checked={selectedIds.includes(opportunity.id)}
                    onChange={() => handleSelect(opportunity.id)}
                    sx={{ marginTop: '0.125rem' }}
                  />

                  <Box
                    sx={{
                      flex: 1,
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '0.75rem',
                    }}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'start',
                        justifyContent: 'space-between',
                        flexDirection: { xs: 'column', sm: 'row' },
                        gap: { xs: '0.5rem', sm: 0 },
                      }}
                    >
                      <Box sx={{ flex: 1, width: { xs: '100%', sm: 'auto' } }}>
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '0.5rem',
                            flexWrap: 'wrap',
                          }}
                        >
                          <Typography
                            sx={{
                              fontSize: { xs: '0.875rem', md: '1rem' },
                              fontWeight: 600,
                              color: '#111827',
                            }}
                          >
                            {opportunity.building} – Unit {opportunity.unit}
                          </Typography>
                          <Chip
                            label={opportunity.confidence.toUpperCase()}
                            size='small'
                            sx={{
                              height: '20px',
                              fontSize: '0.625rem',
                              backgroundColor: confidenceColors.bg,
                              color: confidenceColors.text,
                              border: `1px solid ${confidenceColors.border}`,
                              fontWeight: 600,
                            }}
                          />
                        </Box>
                        <Typography
                          sx={{
                            fontSize: '0.75rem',
                            color: '#6B7280',
                            marginTop: '0.125rem',
                          }}
                        >
                          {opportunity.bed} BR · {opportunity.bath} BA ·{' '}
                          {opportunity.sqft} sqft
                        </Typography>
                      </Box>
                      <Box sx={{ textAlign: 'right' }}>
                        <Typography
                          sx={{
                            fontSize: '0.75rem',
                            color: '#6B7280',
                          }}
                        >
                          Updated {opportunity.lastUpdated}
                        </Typography>
                        {/* <Typography
                          sx={{
                            fontSize: '0.75rem',
                            color: '#6B7280',
                          }}
                        >
                          Next: {opportunity.nextUpdate}
                        </Typography> */}
                      </Box>
                    </Box>

                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: { xs: '0.5rem', sm: '1rem' },
                        flexWrap: 'wrap',
                      }}
                    >
                      <Box>
                        <Typography
                          sx={{
                            fontSize: '0.75rem',
                            color: '#6B7280',
                          }}
                        >
                          Current
                        </Typography>
                        <Typography
                          sx={{
                            fontSize: { xs: '1rem', md: '1.25rem' },
                            fontWeight: 700,
                            color: '#111827',
                          }}
                        >
                          ${opportunity.currentRent.toLocaleString()}
                        </Typography>
                        <Typography
                          sx={{ fontSize: '0.75rem', color: '#A0AEC0' }}
                        >
                          $/SF:{' '}
                          {getRentPerSqft(
                            opportunity.currentRent,
                            opportunity.sqft,
                          )}
                        </Typography>
                      </Box>
                      {(() => {
                        const isPositive =
                          opportunity.recommendedRent > opportunity.currentRent;
                        const isNegative =
                          opportunity.recommendedRent < opportunity.currentRent;
                        const iconColor = isPositive
                          ? '#4FA490'
                          : isNegative
                          ? '#6B7280'
                          : '#6B7280';

                        if (isPositive) {
                          return (
                            <TrendingUp
                              size={20}
                              color={iconColor}
                              style={{ flexShrink: 0 }}
                            />
                          );
                        } else if (isNegative) {
                          return (
                            <Typography
                              sx={{
                                fontSize: '1.25rem',
                                fontWeight: 400,
                                color: iconColor,
                                flexShrink: 0,
                                lineHeight: 1,
                              }}
                            >
                              =
                            </Typography>
                          );
                        } else {
                          return (
                            <TrendingUp
                              size={20}
                              color={iconColor}
                              style={{ flexShrink: 0, opacity: 0.5 }}
                            />
                          );
                        }
                      })()}
                      <Box>
                        <Typography
                          sx={{
                            fontSize: '0.75rem',
                            color: '#6B7280',
                          }}
                        >
                          Recommended
                        </Typography>
                        <Typography
                          sx={{
                            fontSize: { xs: '1rem', md: '1.25rem' },
                            fontWeight: 700,
                            color:
                              opportunity.recommendedRent >
                              opportunity.currentRent
                                ? '#4FA490'
                                : opportunity.recommendedRent <
                                  opportunity.currentRent
                                ? '#6B7280'
                                : '#6B7280',
                          }}
                        >
                          $
                          {(() => {
                            const displayRent =
                              opportunity.recommendedRent <
                              opportunity.currentRent
                                ? 0
                                : opportunity.recommendedRent;
                            return displayRent.toLocaleString();
                          })()}
                        </Typography>
                        <Typography
                          sx={{ fontSize: '0.75rem', color: '#A0AEC0' }}
                        >
                          $/SF:{' '}
                          {(() => {
                            const displayRent =
                              opportunity.recommendedRent <
                              opportunity.currentRent
                                ? 0
                                : opportunity.recommendedRent;
                            return getRentPerSqft(
                              displayRent,
                              opportunity.sqft,
                            );
                          })()}
                        </Typography>
                      </Box>
                      <Box
                        sx={{
                          marginLeft: { xs: 0, sm: '0.5rem' },
                          paddingX: { xs: '0.5rem', sm: '0.75rem' },
                          paddingY: '0.375rem',
                          backgroundColor:
                            Math.max(0, opportunity.delta) > 0
                              ? '#E8F0F1'
                              : '#F3F4F6',
                          borderRadius: '0.5rem',
                          width: { xs: '100%', sm: 'auto' },
                        }}
                      >
                        <Typography
                          sx={{
                            fontSize: '0.75rem',
                            color:
                              Math.max(0, opportunity.delta) > 0
                                ? '#39585C'
                                : '#6B7280',
                          }}
                        >
                          Delta
                        </Typography>
                        <Typography
                          sx={{
                            fontSize: { xs: '1rem', md: '1.125rem' },
                            fontWeight: 700,
                            color:
                              Math.max(0, opportunity.delta) > 0
                                ? '#4FA490'
                                : '#6B7280',
                          }}
                        >
                          {Math.max(0, opportunity.delta) > 0 ? '+' : ''}$
                          {Math.max(0, opportunity.delta)} (
                          {Math.max(0, opportunity.deltaPercent)}%)
                        </Typography>
                      </Box>
                    </Box>

                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.75rem',
                      }}
                    >
                      <Box sx={{ flex: 1 }}>
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            marginBottom: '0.25rem',
                          }}
                        >
                          <Typography
                            sx={{
                              fontSize: '0.75rem',
                              fontWeight: 500,
                              color: '#111827',
                            }}
                          >
                            Comps: &nbsp;
                            {(() => {
                              const comparableBuildings =
                                opportunity.comparableBuildings || [];
                              const customComparableBuildings =
                                opportunity.customComparableBuildings || [];
                              const allBuildingIds = [
                                ...comparableBuildings,
                                ...customComparableBuildings,
                              ];
                              const uniqueCount = new Set(
                                allBuildingIds
                                  .map(id => id?.toString())
                                  .filter(Boolean),
                              ).size;
                              return uniqueCount || opportunity.compsTotal || 0;
                            })()}
                          </Typography>
                          <Typography
                            sx={{
                              fontSize: '0.75rem',
                              color: '#6B7280',
                            }}
                          >
                            {opportunity.compsProgress}%
                          </Typography>
                        </Box>
                        <LinearProgress
                          variant='determinate'
                          value={opportunity.compsProgress}
                          sx={{
                            height: '6px',
                            borderRadius: '3px',
                            backgroundColor: '#E5E7EB',
                            '& .MuiLinearProgress-bar': {
                              backgroundColor: '#4FA490',
                            },
                          }}
                        />
                      </Box>
                      {opportunity.compsProgress < 100 && (
                        <Chip
                          icon={
                            <Box
                              sx={{
                                animation: `${spin} 1s linear infinite`,
                                display: 'inline-flex',
                              }}
                            >
                              <RefreshCw size={12} />
                            </Box>
                          }
                          label='Adding comps'
                          size='small'
                          sx={{
                            fontSize: '0.75rem',
                            borderColor: '#D1D5DB',
                            color: '#374151',
                          }}
                          variant='outlined'
                        />
                      )}
                    </Box>

                    {/* <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'start',
                        gap: '0.5rem',
                        padding: '0.5rem',
                        backgroundColor: '#F3F4F6',
                        borderRadius: '0.5rem',
                      }}
                    >
                      <Info
                        size={14}
                        color='#6B7280'
                        style={{ flexShrink: 0, marginTop: '0.125rem' }}
                      />
                      <Typography
                        sx={{
                          fontSize: '0.75rem',
                          color: '#111827',
                        }}
                      >
                        <strong>Why: </strong>
                        {opportunity.compsCount} comps within{' '}
                        {opportunity.distance} mi; median $
                        {opportunity.recommendedRent}; unit adj{' '}
                        {opportunity.adjustments.join(', ')}
                      </Typography>
                    </Box> */}

                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.5rem',
                        flexWrap: 'wrap',
                      }}
                    >
                      <Button
                        size='small'
                        variant='contained'
                        onClick={() => handleApprove(opportunity)}
                        startIcon={<FileText size={14} />}
                        sx={{
                          backgroundColor: '#39585C',
                          textTransform: 'none',
                          fontSize: { xs: '0.625rem', sm: '0.75rem' },
                          height: '32px',
                          '&:hover': { backgroundColor: '#2A3F42' },
                          whiteSpace: { xs: 'normal', sm: 'nowrap' },
                        }}
                      >
                        <Box
                          component='span'
                          sx={{ display: { xs: 'none', sm: 'inline' } }}
                        >
                          Generate Report
                        </Box>
                        <Box
                          component='span'
                          sx={{ display: { xs: 'inline', sm: 'none' } }}
                        >
                          Approve
                        </Box>
                      </Button>
                      {/* <Button
                        size='small'
                        variant='outlined'
                        onClick={() => handleAdjust(opportunity)}
                        startIcon={<Edit3 size={14} />}
                        sx={{
                          textTransform: 'none',
                          fontSize: '0.75rem',
                          height: '32px',
                          borderColor: '#D1D5DB',
                          color: '#374151',
                        }}
                      >
                        Adjust
                      </Button> */}
                      {/* <Button
                        size='small'
                        variant='outlined'
                        onClick={() => handleSnooze(opportunity)}
                        startIcon={<Clock size={14} />}
                        sx={{
                          textTransform: 'none',
                          fontSize: '0.75rem',
                          height: '32px',
                          borderColor: '#D1D5DB',
                          color: '#374151',
                        }}
                      >
                        Snooze
                      </Button> */}
                      {/* <Button
                        size='small'
                        variant='outlined'
                        onClick={() => handleDismiss(opportunity)}
                        startIcon={<X size={14} />}
                        sx={{
                          textTransform: 'none',
                          fontSize: '0.75rem',
                          height: '32px',
                          borderColor: '#D1D5DB',
                          color: '#374151',
                        }}
                      >
                        Dismiss
                      </Button> */}
                      <Button
                        size='small'
                        variant='text'
                        onClick={() => handleViewComps(opportunity)}
                        startIcon={<Eye size={14} />}
                        sx={{
                          textTransform: 'none',
                          fontSize: '0.75rem',
                          height: '32px',
                          color: '#6B7280',
                        }}
                      >
                        View Comps
                      </Button>
                    </Box>
                  </Box>
                </Box>
              </Card>
            );
          })}
        </Box>
        {pagination.totalPages > 1 && (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              paddingTop: '2rem',
              paddingBottom: '1rem',
            }}
          >
            <Pagination
              count={pagination.totalPages}
              page={page}
              onChange={(event, value) => {
                setPage(value);
                setSelectedIds([]);
                window.scrollTo({ top: 0, behavior: 'smooth' });
              }}
              color='primary'
              sx={{
                '& .MuiPaginationItem-root': {
                  color: '#6B7280',
                  '&.Mui-selected': {
                    backgroundColor: '#39585C',
                    color: '#FFFFFF',
                    '&:hover': {
                      backgroundColor: '#2A3F42',
                    },
                  },
                  '&:hover': {
                    backgroundColor: '#F3F4F6',
                  },
                },
              }}
            />
          </Box>
        )}
      </Box>

      <Dialog
        open={showAdjustModal}
        onClose={() => setShowAdjustModal(false)}
        maxWidth='sm'
        fullWidth
      >
        <DialogTitle>Adjust Target Rent</DialogTitle>
        <DialogContent>
          <Typography
            sx={{
              fontSize: '0.875rem',
              color: '#6B7280',
              marginBottom: '1rem',
            }}
          >
            Override the recommended rent for {selectedOpportunity?.building} –
            Unit {selectedOpportunity?.unit}
          </Typography>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: '1rem',
              paddingTop: '1rem',
            }}
          >
            <TextField
              fullWidth
              label='Target Rent'
              type='number'
              defaultValue={selectedOpportunity?.recommendedRent}
              placeholder='Enter rent amount'
            />
            <FormControl fullWidth>
              <InputLabel>Reason for Adjustment</InputLabel>
              <Select label='Reason for Adjustment' defaultValue=''>
                <MenuItem value='owner'>Owner Override</MenuItem>
                <MenuItem value='vacancy'>Vacancy Risk</MenuItem>
                <MenuItem value='condition'>Unit Condition</MenuItem>
                <MenuItem value='market'>Market Conditions</MenuItem>
                <MenuItem value='other'>Other</MenuItem>
              </Select>
            </FormControl>
            <TextField
              fullWidth
              label='Notes (Optional)'
              multiline
              rows={3}
              placeholder='Add any additional context...'
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setShowAdjustModal(false)}
            sx={{ textTransform: 'none' }}
          >
            Cancel
          </Button>
          <Button
            variant='contained'
            onClick={() => setShowAdjustModal(false)}
            sx={{
              backgroundColor: '#39585C',
              textTransform: 'none',
              '&:hover': { backgroundColor: '#2A3F42' },
            }}
          >
            Save Target
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={showSnoozeModal}
        onClose={() => setShowSnoozeModal(false)}
        maxWidth='sm'
        fullWidth
      >
        <DialogTitle>Snooze Opportunity</DialogTitle>
        <DialogContent>
          <Typography
            sx={{
              fontSize: '0.875rem',
              color: '#6B7280',
              marginBottom: '1rem',
            }}
          >
            When should we remind you about this opportunity?
          </Typography>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: '0.75rem',
              paddingTop: '1rem',
            }}
          >
            <Button
              variant='outlined'
              fullWidth
              onClick={() => setShowSnoozeModal(false)}
              sx={{
                textTransform: 'none',
                justifyContent: 'flex-start',
                borderColor: '#D1D5DB',
                color: '#374151',
              }}
            >
              7 days
            </Button>
            <Button
              variant='outlined'
              fullWidth
              onClick={() => setShowSnoozeModal(false)}
              sx={{
                textTransform: 'none',
                justifyContent: 'flex-start',
                borderColor: '#D1D5DB',
                color: '#374151',
              }}
            >
              14 days
            </Button>
            <Button
              variant='outlined'
              fullWidth
              onClick={() => setShowSnoozeModal(false)}
              sx={{
                textTransform: 'none',
                justifyContent: 'flex-start',
                borderColor: '#D1D5DB',
                color: '#374151',
              }}
            >
              30 days
            </Button>
            <Button
              variant='outlined'
              fullWidth
              onClick={() => setShowSnoozeModal(false)}
              sx={{
                textTransform: 'none',
                justifyContent: 'flex-start',
                borderColor: '#D1D5DB',
                color: '#374151',
              }}
            >
              Custom date...
            </Button>
          </Box>
        </DialogContent>
      </Dialog>

      <Dialog
        open={showDismissModal}
        onClose={() => setShowDismissModal(false)}
        maxWidth='sm'
        fullWidth
      >
        <DialogTitle>Dismiss Opportunity</DialogTitle>
        <DialogContent>
          <Typography
            sx={{
              fontSize: '0.875rem',
              color: '#6B7280',
              marginBottom: '1rem',
            }}
          >
            Why are you dismissing this recommendation?
          </Typography>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: '1rem',
              paddingTop: '1rem',
            }}
          >
            <FormControl fullWidth>
              <InputLabel>Reason</InputLabel>
              <Select label='Reason' defaultValue=''>
                <MenuItem value='inaccurate'>
                  Recommendation Inaccurate
                </MenuItem>
                <MenuItem value='tenant'>Current Tenant Situation</MenuItem>
                <MenuItem value='condition'>Unit Needs Work</MenuItem>
                <MenuItem value='strategy'>Pricing Strategy</MenuItem>
                <MenuItem value='other'>Other</MenuItem>
              </Select>
            </FormControl>
            <TextField
              fullWidth
              label='Notes (Optional)'
              multiline
              rows={3}
              placeholder='Add any additional context...'
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setShowDismissModal(false)}
            sx={{ textTransform: 'none' }}
          >
            Cancel
          </Button>
          <Button
            variant='contained'
            onClick={() => setShowDismissModal(false)}
            sx={{
              backgroundColor: '#EF4444',
              textTransform: 'none',
              '&:hover': { backgroundColor: '#DC2626' },
            }}
          >
            Dismiss
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={showReportModal}
        onClose={() => {
          setShowReportModal(false);
          setReportGenerating(false);
          setReportReady(false);
          setPdfLink(null);
          setSelectedOpportunity(null);
        }}
        maxWidth='md'
        fullWidth
      >
        <DialogTitle>
          {reportGenerating
            ? 'Generating Report...'
            : reportReady
            ? 'Report Ready'
            : 'Generate Report'}
        </DialogTitle>
        <DialogContent>
          <Typography
            sx={{
              fontSize: '0.875rem',
              color: '#6B7280',
              marginBottom: '1rem',
            }}
          >
            {reportGenerating
              ? 'Creating comprehensive pricing justification report'
              : reportReady
              ? 'Your report is ready to download or share'
              : 'Generate a detailed report for this pricing recommendation'}
          </Typography>

          <Box
            sx={{
              position: 'relative',
              display: 'flex',
              flexDirection: 'column',
              gap: '1rem',
              paddingTop: '1rem',
              minHeight: '180px',
            }}
          >
            {reportGenerating && (
              <Box
                sx={{
                  position: 'absolute',
                  inset: 0,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '1rem',
                  backgroundColor: 'rgba(255,255,255,0.8)',
                  zIndex: 1,
                }}
              >
                <Box
                  sx={{
                    animation: `${spin} 1s linear infinite`,
                    display: 'inline-flex',
                  }}
                >
                  <Loader2 size={48} color='#39585C' />
                </Box>
                <Box
                  sx={{
                    textAlign: 'center',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '0.5rem',
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: '0.875rem',
                      fontWeight: 500,
                      color: '#111827',
                    }}
                  >
                    Analyzing comparable units...
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: '0.75rem',
                      color: '#6B7280',
                    }}
                  >
                    This will take a few moments
                  </Typography>
                </Box>
              </Box>
            )}

            <Box
              sx={{
                padding: '1rem',
                backgroundColor: '#F3F4F6',
                borderRadius: '0.5rem',
                display: 'flex',
                flexDirection: 'column',
                gap: '0.5rem',
              }}
            >
              <Typography
                sx={{
                  fontSize: '0.875rem',
                  fontWeight: 600,
                  color: '#111827',
                }}
              >
                Report Contents:
              </Typography>
              <Box
                component='ul'
                sx={{
                  fontSize: '0.75rem',
                  color: '#6B7280',
                  margin: 0,
                  paddingLeft: '1.25rem',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '0.25rem',
                }}
              >
                <li>Unit details and current pricing</li>
                <li>
                  {selectedOpportunity?.compsCount} comparable units analysis
                </li>
                <li>Market positioning and recommendations</li>
                <li>Amenity adjustments breakdown</li>
                <li>Historical pricing performance</li>
              </Box>
            </Box>
          </Box>

          <DialogActions sx={{ paddingTop: '1rem' }}>
            <Button
              onClick={() => {
                setShowReportModal(false);
                setReportGenerating(false);
                setReportReady(false);
                setPdfLink(null);
                setSelectedOpportunity(null);
              }}
              sx={{
                textTransform: 'none',
                color: '#374151',
              }}
            >
              {reportReady ? 'Close' : 'Cancel'}
            </Button>
            <Button
              variant='contained'
              disabled={reportGenerating}
              startIcon={reportReady ? <Download size={16} /> : undefined}
              onClick={() => {
                if (reportReady) {
                  if (pdfLink) {
                    window.open(pdfLink, '_blank');
                  }
                } else {
                  handleGenerateReport();
                }
              }}
              sx={{
                backgroundColor: '#39585C',
                textTransform: 'none',
                '&:hover': { backgroundColor: '#2A3F42' },
              }}
            >
              {reportGenerating
                ? 'Generating...'
                : reportReady
                ? 'Download PDF Report'
                : 'Generate Report'}
            </Button>
            <Button
              variant='outlined'
              startIcon={<Link2 size={16} />}
              onClick={() => {
                if (selectedOpportunity) {
                  const url = `${window.location.origin}/shared-unit/${
                    selectedOpportunity.rentGridId
                  }/${encodeURIComponent(selectedOpportunity.unit)}`;
                  setShareUrl(url);
                  setShowShareModal(true);
                }
              }}
              sx={{
                textTransform: 'none',
                borderColor: '#D1D5DB',
                color: '#374151',
              }}
            >
              Generate Shareable Link
            </Button>
          </DialogActions>
        </DialogContent>
      </Dialog>

      {showInsightDrawer && selectedOpportunity && (
        <InsightDrawer
          opportunity={selectedOpportunity}
          onClose={() => setShowInsightDrawer(false)}
          onUpdate={fetchOpportunities}
        />
      )}

      <Dialog
        open={showShareModal}
        onClose={() => setShowShareModal(false)}
        maxWidth='sm'
        fullWidth
      >
        <DialogTitle>Shareable Link</DialogTitle>
        <DialogContent>
          <Typography
            sx={{
              fontSize: '0.875rem',
              color: '#6B7280',
              marginBottom: '1rem',
            }}
          >
            Copy this link to share the unit view. Anyone with this link can
            view the unit details and map.
          </Typography>
          <TextField
            fullWidth
            value={shareUrl || ''}
            InputProps={{
              readOnly: true,
            }}
            onClick={e => e.target.select()}
            sx={{
              '& .MuiInputBase-input': {
                fontSize: '0.875rem',
                fontFamily: 'monospace',
              },
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setShowShareModal(false)}
            sx={{ textTransform: 'none' }}
          >
            Close
          </Button>
          <Button
            variant='contained'
            onClick={() => {
              if (shareUrl) {
                navigator.clipboard.writeText(shareUrl);
                alert('Link copied to clipboard!');
              }
            }}
            sx={{
              backgroundColor: '#39585C',
              textTransform: 'none',
              '&:hover': { backgroundColor: '#2A3F42' },
            }}
          >
            Copy Link
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={showRecalculateDialog}
        onClose={() => setShowRecalculateDialog(false)}
        maxWidth='sm'
        fullWidth
      >
        <DialogTitle>New Comparables Available</DialogTitle>
        <DialogContent>
          <Typography
            sx={{
              fontSize: '0.875rem',
              color: '#6B7280',
              marginBottom: '1rem',
            }}
          >
            You have customized the comps and there are new comps available.
            Recalculate?
          </Typography>
          {recalculating && (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: '1rem',
                marginTop: '1rem',
              }}
            >
              <CircularProgress size={20} />
              <Typography
                sx={{
                  fontSize: '0.875rem',
                  color: '#6B7280',
                }}
              >
                Recalculating rent grids...
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setShowRecalculateDialog(false)}
            disabled={recalculating}
            sx={{ textTransform: 'none' }}
          >
            Later
          </Button>
          <Button
            variant='contained'
            onClick={handleManualRecalculate}
            disabled={recalculating}
            sx={{
              backgroundColor: '#39585C',
              textTransform: 'none',
              '&:hover': { backgroundColor: '#2A3F42' },
            }}
          >
            Recalculate
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ActionInbox;
